import axiosInstance from '../lib/axios'
import { API_ENDPOINTS } from '../constants/api'
import type {
  MonitoringOverview,
  ServiceStatus,
  APIPerformanceResponse,
  ErrorListResponse,
  SystemResources,
} from '../types/api.types'

export const monitoringService = {
  async getOverview(): Promise<MonitoringOverview> {
    const { data } = await axiosInstance.get<MonitoringOverview>(
      API_ENDPOINTS.PLATFORM_MONITORING_OVERVIEW,
    )
    return data
  },

  async getServices(): Promise<ServiceStatus[]> {
    const { data } = await axiosInstance.get<ServiceStatus[]>(
      API_ENDPOINTS.PLATFORM_MONITORING_SERVICES,
    )
    return data
  },

  async getPerformance(windowHours: 1 | 6 | 24 = 1): Promise<APIPerformanceResponse> {
    const { data } = await axiosInstance.get<APIPerformanceResponse>(
      `${API_ENDPOINTS.PLATFORM_MONITORING_PERFORMANCE}?window_hours=${windowHours}`,
    )
    return data
  },

  async getErrors(params?: {
    source?: 'backend' | 'frontend'
    severity?: 'ERROR' | 'WARNING' | 'CRITICAL'
    is_resolved?: boolean
    limit?: number
    offset?: number
  }): Promise<ErrorListResponse> {
    const qs = new URLSearchParams()
    if (params?.source) qs.set('source', params.source)
    if (params?.severity) qs.set('severity', params.severity)
    if (params?.is_resolved !== undefined) qs.set('is_resolved', String(params.is_resolved))
    if (params?.limit) qs.set('limit', String(params.limit))
    if (params?.offset) qs.set('offset', String(params.offset))
    const { data } = await axiosInstance.get<ErrorListResponse>(
      `${API_ENDPOINTS.PLATFORM_MONITORING_ERRORS}${qs.toString() ? `?${qs}` : ''}`,
    )
    return data
  },

  async resolveError(id: number): Promise<void> {
    await axiosInstance.patch(API_ENDPOINTS.PLATFORM_MONITORING_ERROR_RESOLVE(id))
  },

  async getSystem(): Promise<SystemResources> {
    const { data } = await axiosInstance.get<SystemResources>(
      API_ENDPOINTS.PLATFORM_MONITORING_SYSTEM,
    )
    return data
  },

  async reportFrontendError(payload: {
    severity: 'ERROR' | 'WARNING' | 'CRITICAL'
    endpoint: string | null
    message: string
    stack_trace: string | null
    extra_data?: Record<string, unknown> | null
  }): Promise<void> {
    const token = sessionStorage.getItem('auth_token')
    if (!token) return
    // Use raw fetch — never use the axios instance here (risk of infinite loop if axios itself errors)
    await fetch(API_ENDPOINTS.PLATFORM_MONITORING_ERRORS, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ source: 'frontend', ...payload }),
    }).catch(() => { /* never crash inside error reporting */ })
  },
}
